import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import useVehicleBalanceTransactions from '../hooks/useVehicleBalanceTransactions';

const VehicleBalanceTransactions = ({ vehicleId }) => {
  const { transactions, isLoading, error, message, refetch } = useVehicleBalanceTransactions(vehicleId);
  const [localTransactions, setLocalTransactions] = useState([]);

  // Show server message as toast
  useEffect(() => {
    if (message) {
      console.log("Server message:", message);
      toast.success(message);
    }
  }, [message]);

  // Clean data
  useEffect(() => {
    if (transactions) {
      const cleaned = transactions.map(t => ({
        ...t,
        amount: t.amount || 0,
        created_at: t.created_at ? new Date(t.created_at).toLocaleString() : '',
        transaction_type: t.transaction_type || '',
        reference_id: t.reference_id || '',
        vehicle_id: t.vehicle_id || '',
      }));
      setLocalTransactions(cleaned);
    }
  }, [transactions]);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    dateRange: '30'
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', type: 'all', dateRange: '30' });
  };

  const filteredTransactions = localTransactions.filter(tx => {
    // Search filter
    if (filters.search && !tx.id?.toString().toLowerCase().includes(filters.search.toLowerCase())) return false;

    // Type filter
    if (filters.type !== 'all' && tx.transaction_type !== filters.type) return false;

    // Date range filter
    const txDate = new Date(tx.created_at);
    const today = new Date();
    if (filters.dateRange === 'today') {
      if (
        txDate.getDate() !== today.getDate() ||
        txDate.getMonth() !== today.getMonth() ||
        txDate.getFullYear() !== today.getFullYear()
      ) return false;
    } else if (filters.dateRange !== 'all') {
      const days = parseInt(filters.dateRange);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - days);
      if (txDate < cutoffDate) return false;
    }

    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-full mx-auto px-2 sm:px-2 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <h1 className="text-2xl font-semibold tracking-tight text-ink">Vehicle Balance Transactions</h1>
            <div className="flex items-center space-x-4">
              <button 
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                onClick={refetch}
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-2 sm:px-0 lg:px-0 py-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-4">
          <input
            type="text"
            placeholder="Search by ID"
            className="border px-3 py-2 rounded-lg"
            value={filters.search}
            onChange={e => handleFilterChange('search', e.target.value)}
          />
          <select
            className="border px-3 py-2 rounded-lg"
            value={filters.type}
            onChange={e => handleFilterChange('type', e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="debit">Debit</option>
            <option value="credit">Credit</option>
          </select>
          <select
            className="border px-3 py-2 rounded-lg"
            value={filters.dateRange}
            onChange={e => handleFilterChange('dateRange', e.target.value)}
          >
            <option value="30">Last 30 days</option>
            <option value="7">Last 7 days</option>
            <option value="1">Today</option>
            <option value="all">All time</option>
          </select>
          <button
            className="bg-gray-200 px-3 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-900">Recent Vehicle Balance Transactions</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((tx, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{tx.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.amount}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{tx.transaction_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.reference_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.vehicle_id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tx.created_at}</td>
                  </tr>
                ))}

                {filteredTransactions.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                )}

                {isLoading && (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                      Loading...
                    </td>
                  </tr>
                )}

                {/* {error && (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-red-500">
                      {error}
                    </td>
                  </tr>
                )} */}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VehicleBalanceTransactions;
